import React, { useState, useEffect, useCallback } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Folder, FolderOpen, FolderPlus, Trash2, Pencil, ChevronRight, ChevronDown, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const FolderNode = ({
  node, depth, selectedId, onSelect, onRefresh, getAuthHeaders, expandedSet, toggleExpanded
}) => {
  const isOpen = expandedSet.has(node.id);
  const isSelected = selectedId === node.id;
  const hasChildren = (node.children || []).length > 0;

  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.name);
  const [creatingChild, setCreatingChild] = useState(false);
  const [childName, setChildName] = useState('');

  const handleRename = useCallback(async () => {
    const name = renameValue.trim();
    if (!name || name === node.name) { setRenaming(false); return; }
    try {
      await api.renameFolder(node.id, { name }, getAuthHeaders());
      toast.success('Folder renamed');
      setRenaming(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    }
  }, [renameValue, node, getAuthHeaders, onRefresh]);

  const handleCreateChild = useCallback(async () => {
    const name = childName.trim();
    if (!name) { setCreatingChild(false); return; }
    try {
      await api.createFolder({ name, parent_id: node.id }, getAuthHeaders());
      toast.success('Subfolder created');
      setChildName('');
      setCreatingChild(false);
      // Auto-expand parent
      if (!isOpen) toggleExpanded(node.id);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  }, [childName, node, getAuthHeaders, onRefresh, isOpen, toggleExpanded]);

  const handleDelete = useCallback(async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete folder "${node.name}"? Only empty folders can be deleted.`)) return;
    try {
      await api.deleteFolder(node.id, getAuthHeaders());
      toast.success('Folder deleted');
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  }, [node, getAuthHeaders, onRefresh]);

  return (
    <Collapsible open={isOpen} onOpenChange={() => toggleExpanded(node.id)}>
      <div
        className={`group flex items-center gap-1 px-2 py-1 rounded text-sm cursor-pointer hover:bg-slate-100 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        onClick={() => onSelect(node.id)}
        onDoubleClick={(e) => { e.stopPropagation(); setRenaming(true); setRenameValue(node.name); }}
        data-testid={`folder-node-${node.id}`}
      >
        <CollapsibleTrigger asChild>
          <button
            onClick={(e) => { e.stopPropagation(); toggleExpanded(node.id); }}
            className="p-0.5 text-slate-400 hover:text-slate-700"
            data-testid={`folder-toggle-${node.id}`}
          >
            {hasChildren ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="inline-block w-3.5" />}
          </button>
        </CollapsibleTrigger>

        {isOpen ? <FolderOpen className="w-4 h-4 text-amber-500 shrink-0" /> : <Folder className="w-4 h-4 text-amber-500 shrink-0" />}

        {renaming ? (
          <Input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
            onBlur={handleRename}
            onClick={(e) => e.stopPropagation()}
            className="h-6 text-xs px-1 flex-1"
            data-testid={`folder-rename-input-${node.id}`}
          />
        ) : (
          <span className="truncate flex-1" title={node.path}>{node.name}</span>
        )}

        <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setCreatingChild(true); }}
            className="p-0.5 text-slate-400 hover:text-blue-600"
            title="Add subfolder"
            data-testid={`folder-add-child-${node.id}`}
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setRenaming(true); setRenameValue(node.name); }}
            className="p-0.5 text-slate-400 hover:text-slate-700"
            title="Rename"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleDelete}
            className="p-0.5 text-slate-400 hover:text-red-600"
            title="Delete (empty only)"
            data-testid={`folder-delete-${node.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {creatingChild && (
        <div className="flex items-center gap-1 py-1" style={{ paddingLeft: `${22 + (depth + 1) * 14}px` }}>
          <FolderPlus className="w-3.5 h-3.5 text-slate-400" />
          <Input
            autoFocus
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateChild(); if (e.key === 'Escape') setCreatingChild(false); }}
            onBlur={() => { if (childName.trim()) handleCreateChild(); else setCreatingChild(false); }}
            placeholder="New folder name"
            className="h-6 text-xs px-1"
            data-testid={`folder-new-child-input-${node.id}`}
          />
        </div>
      )}

      <CollapsibleContent>
        {(node.children || []).map((child) => (
          <FolderNode
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            onRefresh={onRefresh}
            getAuthHeaders={getAuthHeaders}
            expandedSet={expandedSet}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const FolderTree = ({ selectedFolderId, onSelectFolder }) => {
  const { getAuthHeaders } = useAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [creatingRoot, setCreatingRoot] = useState(false);
  const [rootName, setRootName] = useState('');

  // loadTree is stable; we manually call it after CRUD. Initial load happens once.
  const loadTree = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.listFolders(getAuthHeaders());
      setTree(res.data?.folders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  // getAuthHeaders is recreated each render in AuthContext; intentionally omitted
  // from deps to avoid an effect/refresh loop. We resolve headers inline.
  // eslint-disable-next-line
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const toggleExpanded = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleCreateRoot = async () => {
    const name = rootName.trim();
    if (!name) { setCreatingRoot(false); return; }
    try {
      await api.createFolder({ name }, getAuthHeaders());
      toast.success('Folder created');
      setRootName('');
      setCreatingRoot(false);
      loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <div data-testid="folder-tree" className="text-sm">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-200 mb-2">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Folders</span>
        <button
          onClick={() => setCreatingRoot(true)}
          className="p-1 text-slate-500 hover:text-blue-600"
          title="New folder"
          data-testid="folder-new-root-btn"
        >
          <FolderPlus className="w-4 h-4" />
        </button>
      </div>

      {/* All documents (root) */}
      <div
        className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer hover:bg-slate-100 ${selectedFolderId === null ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
        onClick={() => onSelectFolder(null)}
        data-testid="folder-all-documents"
      >
        <Home className="w-4 h-4 text-slate-500" />
        <span>All Documents</span>
      </div>

      {creatingRoot && (
        <div className="flex items-center gap-1 py-1 px-2">
          <FolderPlus className="w-3.5 h-3.5 text-slate-400" />
          <Input
            autoFocus
            value={rootName}
            onChange={(e) => setRootName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateRoot(); if (e.key === 'Escape') setCreatingRoot(false); }}
            onBlur={() => { if (rootName.trim()) handleCreateRoot(); else setCreatingRoot(false); }}
            placeholder="New folder name"
            className="h-7 text-xs px-1.5"
            data-testid="folder-new-root-input"
          />
        </div>
      )}

      {loading ? (
        <div className="text-xs text-slate-400 px-2 py-4 text-center">Loading…</div>
      ) : tree.length === 0 && !creatingRoot ? (
        <div className="text-xs text-slate-400 px-2 py-6 text-center">
          No folders yet.<br />Click <FolderPlus className="w-3 h-3 inline-block" /> to create one.
        </div>
      ) : (
        <div className="space-y-0.5">
          {tree.map((root) => (
            <FolderNode
              key={root.id}
              node={root}
              depth={0}
              selectedId={selectedFolderId}
              onSelect={onSelectFolder}
              onRefresh={loadTree}
              getAuthHeaders={getAuthHeaders}
              expandedSet={expanded}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderTree;
